import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHeart, FaShare, FaComment, FaClock, FaUsers, FaChartBar } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { mockRecipes } from '../data/mockData';
import CommentSection from '../components/CommentSection';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    // Find recipe by ID (in real app, this would be an API call)
    const foundRecipe = mockRecipes.find(r => r.id === parseInt(id));
    if (foundRecipe) {
      setRecipe(foundRecipe);
      setLikesCount(foundRecipe.likes);
      setIsLiked(false);
    } else {
      navigate('/feed');
    }
  }, [id, navigate]);

  // Auto-open comments if navigated with #comments
  useEffect(() => {
    if (location.hash === '#comments') {
      setShowComments(true);
      setTimeout(() => {
        const el = document.getElementById('comments');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location]);

  const handleLike = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const [showShare, setShowShare] = useState(false);
  const handleShareNative = () => {
    if (navigator.share) {
      navigator.share({ title: recipe.title, text: `Check out this amazing recipe: ${recipe.title}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Recipe link copied to clipboard!');
    }
  };
  const shareToWhatsapp = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`Check out this recipe: ${recipe.title} - `);
    window.open(`https://api.whatsapp.com/send?text=${text}${url}`, '_blank');
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setShowShare(false);
    setToast('Link copied to clipboard');
    setTimeout(() => setToast(''), 1500);
  };

  if (!recipe) {
    return (
      <div className="page" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="card" style={{ padding: 16 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Hero Image */}
      <div style={{ position: 'relative', height: 384, background: '#e5e7eb', overflow: 'hidden' }}>
        <img src={recipe.image} alt={recipe.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-end' }}>
          <div className="container" style={{ padding: 24, color: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <button onClick={() => navigate(-1)} className="btn" style={{ background: '#ffffff', color: '#111827', borderRadius: 999, padding: '8px 12px', fontWeight: 700 }}>← Back</button>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, margin: '12px 0 8px' }}>
              {recipe.title}
            </motion.h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, fontSize: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaClock /> <span>{recipe.cookTime}</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaUsers /> <span>{recipe.servings} servings</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaChartBar /> <span>{recipe.difficulty}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 24 }}>
        <div style={{ display: 'grid', gap: 24, gridTemplateColumns: '1fr', alignItems: 'start' }}>
          {/* Main Content */}
          <div style={{ display: 'grid', gap: 24 }}>
            {/* Action Buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={handleLike} className="btn" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: isLiked ? '#fee2e2' : '#f3f4f6', color: isLiked ? '#b91c1c' : '#6b7280' }}>
                <FaHeart /> <span>{likesCount}</span>
              </button>
              <button onClick={() => { setShowComments(v => !v); setTimeout(() => { if (!showComments) { const el = document.getElementById('comments'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } }, 50); }} className="btn" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>
                <FaComment /> <span>{recipe.comments}</span>
              </button>
              <div className="share-menu">
                <button onClick={() => setShowShare(v => !v)} className="btn" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#f3f4f6', color: '#6b7280' }}>
                  <FaShare /> <span>{recipe.shares}</span>
                </button>
                {showShare && (
                  <div className="share-pop">
                    <button onClick={handleShareNative}>Share (System)</button>
                    <button onClick={shareToWhatsapp}>Share to WhatsApp</button>
                    <button onClick={copyLink}>Copy Link</button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card">
              <h2 style={{ marginTop: 0 }}>About This Recipe</h2>
              <p style={{ color: '#6b7280', lineHeight: 1.7 }}>{recipe.description}</p>
            </motion.div>

            {/* Ingredients */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card">
              <h2 style={{ marginTop: 0 }}>Ingredients</h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                {recipe.ingredients.map((ingredient, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 8, height: 8, background: 'var(--brand-600)', borderRadius: 999 }}></div>
                    <span style={{ color: '#374151' }}>{ingredient}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Instructions */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card">
              <h2 style={{ marginTop: 0 }}>Instructions</h2>
              <ol style={{ padding: 0, margin: 0, display: 'grid', gap: 12 }}>
                {recipe.instructions.map((instruction, index) => (
                  <li key={index} style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flexShrink: 0, width: 32, height: 32, background: 'var(--brand-600)', color: '#fff', borderRadius: 999, display: 'grid', placeItems: 'center', fontWeight: 700 }}>{index + 1}</div>
                    <p style={{ color: '#374151', margin: '4px 0 0' }}>{instruction}</p>
                  </li>
                ))}
              </ol>
            </motion.div>

            {/* Comments Section */}
            {showComments && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <CommentSection recipeId={recipe.id} />
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display: 'grid', gap: 24 }}>
            {/* Nutrition Facts */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="card">
              <h3 style={{ marginTop: 0 }}>Nutrition Facts</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                {Object.entries(recipe.nutrition).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', textTransform: 'capitalize' }}>{key}</span>
                    <span style={{ fontWeight: 700, color: '#111827' }}>{value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tags */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="card">
              <h3 style={{ marginTop: 0 }}>Tags</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {recipe.tags.map((tag, index) => (
                  <span key={index} className="btn" style={{ padding: '6px 10px', background: '#e8f7ef', color: 'var(--brand-600)', borderRadius: 999, fontSize: 14, fontWeight: 700 }}>
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Recipe Author */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }} className="card">
              <h3 style={{ marginTop: 0 }}>Recipe by</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={recipe.author.avatar} alt={recipe.author.name} style={{ width: 48, height: 48, borderRadius: 999 }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>{recipe.author.name}</p>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>{recipe.author.recipes} recipes</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 14px', borderRadius: 999, boxShadow: '0 10px 20px rgba(0,0,0,0.2)', zIndex: 1000 }}>{toast}</div>
      )}

      {/* Responsive: switch to 2-column at >= 1000px */}
      <style>{`@media (min-width: 1000px){ .container + div > div { grid-template-columns: 2fr 1fr; } }`}</style>
    </div>
  );
};

export default RecipeDetail;
